import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
  EventEmitter,
  Output,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CdkMenuTrigger } from '@angular/cdk/menu';
import { XCommonToolbarItemComponent } from '../../../../../shared/x-kit/components/x-common-toolbar-item/x-common-toolbar-item.component';
import { XIconButtonComponent } from '../../../../../shared/x-kit/components/x-icon-button/x-icon-button.component';
import { XIconButtonSelectorComponent } from '../../../../../shared/x-kit/components/x-icon-button-selector/x-icon-button-selector.component';
import { XButtonComponent } from '../../../../../shared/x-kit/components/x-button/x-button.component';
import { XMenuComponent, TMenuItem } from '../../../../../shared/x-kit/components/x-menu/x-menu.component';
import { PopoverLayoutComponent } from '../../../../../map-modules/external-entities/shared/popover-layout/popover-layout.component';
import { SvgIconComponent } from 'angular-svg-icon';
import { DashboardApiService } from '../../../../../core/services/dashboard-api.service';
import { DashboardTemplate, ReportDetail, ReportListItem } from '../../../../../core/models/dashboard.model';
import { InputDialogService } from '../../../../../shared/components/input-dialog';
import { take, forkJoin, takeUntil, ReplaySubject } from 'rxjs';
import { dashError, dashGroupCollapsed, dashGroupEnd, dashLog, dashWarn, safeStringify } from '../../../../../core/utils/dashboard-debug';
import { DetailedPlotSettings } from '../../../../../shared/services/api/boards-api.service';
import { MetricService } from '../../../../../core/services/metric.service';
import { UserJourneyEvent } from '../../../../../core/services/user-journey.types';

@Component({
  selector: 'app-dashboard-toolbar',
  standalone: true,
  imports: [
    CommonModule,
    PopoverLayoutComponent,
    XCommonToolbarItemComponent,
    XIconButtonComponent,
    XIconButtonSelectorComponent,
    XButtonComponent,
    SvgIconComponent,
    CdkMenuTrigger,
    XMenuComponent,
    TranslatePipe,
  ],
  templateUrl: './dashboard-toolbar.component.html',
  styleUrl: './dashboard-toolbar.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardToolbarComponent implements OnInit, OnDestroy {
  public translate = inject(TranslateService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private dashboardApiService = inject(DashboardApiService);
  private inputDialog = inject(InputDialogService);
  private cdr = inject(ChangeDetectorRef);
  private metricService = inject(MetricService);
  private destroy$ = new ReplaySubject<void>();

  public boardName = 'Valuation Dashboard';
  public reports: ReportListItem[] = [];
  public isLoadingReports = false;

  @Output() public reportChanged = new EventEmitter<ReportDetail>();
  @Output() public exportPdfClick = new EventEmitter<void>();
  @Output() public settingsChange = new EventEmitter<DetailedPlotSettings>();
  @Output() public toggleCaseAssetInfo = new EventEmitter<void>();

  public detailedSettingItems = [
    {
      id: 'isShowDetailedDimensions',
      text: 'Detailed dimensions',
      isToggle: true,
      toggleValue: true,
      disabled: false,
    },
    {
      id: 'isShowNorthAngle',
      text: 'North angle',
      isToggle: true,
      toggleValue: true,
      disabled: false,
    },
    {
      id: 'isShowBoundaries',
      text: 'Boundaries',
      isToggle: true,
      toggleValue: true,
      disabled: false,
    },
    {
      id: 'parcelFollowingEnabled',
      text: 'Parcel following',
      isToggle: true,
      toggleValue: false,
      disabled: false,
    },
  ];

  public onExportPdfClick(): void {
    console.log('[DashboardToolbar] onExportPdfClick called');
    this.exportPdfClick.emit();
  }

  public onToggleCaseAssetInfoClick(): void {
    this.toggleCaseAssetInfo.emit();
  }

  ngOnInit() {
    // Load reports when component initializes
    this.loadReports();
    // Load translations for detailed settings
    this.getDetailedDimensionItems();
    // Subscribe to language changes
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.getDetailedDimensionItems();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getDetailedDimensionItems() {
    forkJoin([
      this.translate.get('detailed_dimensions'),
      this.translate.get('north_angle'),
      this.translate.get('boundaries'),
      this.translate.get('parcel_following'),
    ]).pipe(takeUntil(this.destroy$)).subscribe((translations) => {
      this.detailedSettingItems = [
        {
          id: 'isShowDetailedDimensions',
          text: translations[0],
          isToggle: true,
          toggleValue: this.detailedSettingItems.find((it) => it.id === 'isShowDetailedDimensions')?.toggleValue ?? true,
          disabled: false,
        },
        {
          id: 'isShowNorthAngle',
          text: translations[1],
          isToggle: true,
          toggleValue: this.detailedSettingItems.find((it) => it.id === 'isShowNorthAngle')?.toggleValue ?? true,
          disabled: false,
        },
        {
          id: 'isShowBoundaries',
          text: translations[2],
          isToggle: true,
          toggleValue: this.detailedSettingItems.find((it) => it.id === 'isShowBoundaries')?.toggleValue ?? true,
          disabled: false,
        },
        {
          id: 'parcelFollowingEnabled',
          text: translations[3],
          isToggle: true,
          toggleValue: this.detailedSettingItems.find((it) => it.id === 'parcelFollowingEnabled')?.toggleValue ?? false,
          disabled: false,
        },
      ];
      this.cdr.markForCheck();
    });
  }

  public onToggleChange(evt: TMenuItem) {
    const item = this.detailedSettingItems.find((it) => it.id === evt.id);
    if (item) {
      item.toggleValue = !item.toggleValue;
      
      // Emit settings change
      const settings: DetailedPlotSettings = {
        isShowDetailedDimensions: this.detailedSettingItems.find((it) => it.id === 'isShowDetailedDimensions')?.toggleValue ?? true,
        isShowNorthAngle: this.detailedSettingItems.find((it) => it.id === 'isShowNorthAngle')?.toggleValue ?? true,
        isShowBoundaries: this.detailedSettingItems.find((it) => it.id === 'isShowBoundaries')?.toggleValue ?? true,
        parcelFollowingEnabled: this.detailedSettingItems.find((it) => it.id === 'parcelFollowingEnabled')?.toggleValue ?? false,
      };
      
      this.settingsChange.emit(settings);
      this.cdr.markForCheck();
    }
  }

  private getProjectId(): string | null {
    return this.route.snapshot.paramMap.get('project_id') ||
           this.route.snapshot.paramMap.get('projectId') || 
           this.router.url.match(/\/projects\/([^\/]+)\//)?.[1] || 
           null;
  }

  private getDashboardId(): string | null {
    return this.route.snapshot.paramMap.get('boardId') ||
           this.route.snapshot.paramMap.get('dashboardId') || 
           this.router.url.match(/\/dashboard\/([^\/]+)/)?.[1] || 
           null;
  }

  public onMenuItemClick(item: TMenuItem) {
    if (item.id === 'home') {
      // Navigate back to cases list
      const projectId = this.getProjectId();
      
      if (projectId) {
        this.router.navigate(['projects', projectId, 'cases']);
      } else {
        this.router.navigate(['projects/']);
      }
    } else if (item.id === 'create-template') {
      this.createDashboardTemplate();
    } else if (item.id === 'create-dashboard') {
      this.createDashboard();
    } else if (item.id === 'generate-report') {
      this.generateReport();
    } else if (item.id === 'load-latest-report') {
      this.loadLatestReport();
    } else if (item.id === 'view-reports') {
      this.loadReports();
    }
  }

  public onLanguageChange(item: TMenuItem) {
    this.translate.use(item.id);
    localStorage.setItem('current_lang', item.id);
    this.metricService.track({
      name: `language_selected_${item.id}`
    });
  }

  /**
   * Create dashboard template with all widgets and their endpoints
   * Based on documentation: Bank Valuation Dashboard template
   */
  private createDashboardTemplate() {
    dashLog('DashboardToolbar: creating dashboard template');
    
    // Get projectId from URL
    const projectId = this.getProjectId();
    dashLog('DashboardToolbar: projectId resolved', { projectId });
    
    if (!projectId) {
      dashWarn('DashboardToolbar: Project ID not found');
      return;
    }
    
    // Bank Valuation Dashboard template with 4 widgets according to documentation
    const template: DashboardTemplate = {
      title: 'Bank Valuation Dashboard',
      description: 'Complete bank valuation dashboard with market value, comparables, FSV, and reliability metrics',
      version: '1.0.0',
      widgetConfigs: [
        {
          widgetId: '019b2b3e-9c74-7481-bea7-fd3578f56556', // Market Value Widget
          version: '1.0.0',
          position: {
            x: 0,
            y: 0,
            w: 6,
            h: 4
          },
          config: {}
        },
        {
          widgetId: '019b2b3e-f94d-72bb-945e-7e558a977d9b', // Appreciation Rate (Comparables) Widget
          version: '1.0.0',
          position: {
            x: 6,
            y: 0,
            w: 6,
            h: 4
          },
          config: {}
        },
        {
          widgetId: '019b2b3f-431e-72b9-a12c-e4e78a82fd8e', // Forced Sale Value (FSV) Widget
          version: '1.0.0',
          position: {
            x: 0,
            y: 4,
            w: 6,
            h: 4
          },
          config: {}
        },
        {
          widgetId: '019b2b3f-d037-7719-8753-9630ac64bf10', // Reliability of Valuation Widget
          version: '1.0.0',
          position: {
            x: 6,
            y: 4,
            w: 6,
            h: 4
          },
          config: {}
        }
      ]
    };

    this.dashboardApiService.createTemplate(template, projectId).subscribe({
      next: (createdTemplate) => {
        dashLog('DashboardToolbar: template created', { 
          uuid: (createdTemplate as any)?.uuid || (createdTemplate as any)?.id, 
          title: (createdTemplate as any)?.title 
        });
        dashWarn('DashboardToolbar: Template created successfully');
      },
      error: (error) => {
        dashError('DashboardToolbar: failed to create template', { 
          message: (error as any)?.message, 
          status: (error as any)?.status, 
          response: safeStringify((error as any)?.error) 
        });
      }
    });
  }

  /**
   * Create a new dashboard from template
   */
  private createDashboard() {
    dashLog('DashboardToolbar: creating dashboard');
    
    const projectId = this.getProjectId();
    if (!projectId) {
      dashWarn('DashboardToolbar: Project ID not found');
      return;
    }

    // Ask user for plot UUID
    this.inputDialog.open({
      title: 'Create Dashboard',
      label: 'Enter plot UUID:',
      placeholder: '019808aa-5ad9-7a1b-b25b-872ada44ada5',
      initialValue: '',
      primaryButton: { text: 'Create', style: 'filled-black' },
      secondaryButton: { text: 'Cancel', style: 'outlined' }
    }).pipe(take(1)).subscribe((result) => {
      console.log('[DashboardToolbar] Dialog result:', result);
      
      if (result && result.confirmed && result.value) {
        const plotUuid = result.value.trim();
        console.log('[DashboardToolbar] Plot UUID from dialog:', plotUuid);
        
        if (!plotUuid) {
          dashWarn('DashboardToolbar: plot UUID is required');
          return;
        }

        dashGroupCollapsed('DashboardToolbar: createDashboard', { projectId, plotUuid });

        const request: any = {
          templateId: '019b2b41-2848-72a4-8dde-28dc09b67c96',
          title: 'My First Bank Valuation Dashboard',
          uuid: plotUuid,
          sapi: 1
        };

        this.dashboardApiService.createDashboard(projectId, request).subscribe({
          next: (dashboard) => {
            const dashboardId = (dashboard as any)?.id || (dashboard as any)?.uuid;
            dashLog('DashboardToolbar: dashboard created', { 
              id: dashboardId, 
              title: (dashboard as any)?.title 
            });
            
            // Redirect to the newly created dashboard
            if (dashboardId) {
              this.router.navigate(['projects', projectId, 'dashboard', dashboardId]);
            }
            dashGroupEnd();
          },
          error: (error) => {
            dashError('DashboardToolbar: failed to create dashboard', { 
              message: (error as any)?.message, 
              status: (error as any)?.status, 
              response: safeStringify((error as any)?.error) 
            });
            dashGroupEnd();
          }
        });
      }
    });
  }

  /**
   * Generate a report for the current dashboard
   */
  private generateReport() {
    const projectId = this.getProjectId();
    const dashboardId = this.getDashboardId();

    if (!projectId || !dashboardId) {
      alert('Project ID or Dashboard ID not found');
      return;
    }

    // Ask user for project UUID for the report
    this.inputDialog.open({
      title: 'Generate Report',
      label: 'Enter plot UUID for report:',
      placeholder: 'plot-uuid',
      initialValue: '',
      primaryButton: { text: 'Generate', style: 'filled-black' },
      secondaryButton: { text: 'Cancel', style: 'outlined-black' },
    }).pipe(take(1)).subscribe((result) => {
      if (result && result.confirmed && result.value) {
        const projectUuidForReport = result.value.trim();
        
        if (!projectUuidForReport) {
          alert('Project UUID is required');
          return;
        }

        dashGroupCollapsed('DashboardToolbar: generateReport', { projectId, dashboardId });

        this.dashboardApiService.generateReport(projectId, dashboardId).subscribe({
          next: (report) => {
            dashLog('DashboardToolbar: report generated', { id: (report as any)?.id, status: (report as any)?.status, plotUuid: (report as any)?.plotUuid ?? (report as any)?.data?.plotUuid });
            alert(`Report generated successfully!`);
            // Reload reports list
            this.loadReports();
            // Notify dashboard page to re-render using this report
            this.reportChanged.emit(report as ReportDetail);
            dashGroupEnd();
          },
          error: (error) => {
            dashError('DashboardToolbar: failed to generate report', { message: (error as any)?.message, status: (error as any)?.status, response: safeStringify((error as any)?.error) });
            alert(`Error generating report: ${error.message || 'Unknown error'}`);
            dashGroupEnd();
          }
        });
      }
    });
  }

  /**
   * Load reports for the current dashboard
   */
  private loadReports() {
    const projectId = this.getProjectId();
    const dashboardId = this.getDashboardId();

    if (!projectId || !dashboardId) {
      dashWarn('DashboardToolbar: cannot load reports (missing ids)', { projectId, dashboardId });
      return;
    }

    this.isLoadingReports = true;
    this.cdr.markForCheck();

    dashLog('DashboardToolbar: loading reports', { projectId, dashboardId });

    this.dashboardApiService.getReports(projectId, dashboardId).subscribe({
      next: (reports) => {
        dashLog('DashboardToolbar: reports loaded', { count: Array.isArray(reports) ? reports.length : 0 });
        this.reports = Array.isArray(reports) ? reports : [];
        this.isLoadingReports = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        dashError('DashboardToolbar: failed to load reports', { message: (error as any)?.message, status: (error as any)?.status, response: safeStringify((error as any)?.error) });
        this.isLoadingReports = false;
        this.cdr.markForCheck();
        alert(`Error loading reports: ${error.message || 'Unknown error'}`);
      }
    });
  }

  /**
   * Load the latest report for the current dashboard
   */
  private loadLatestReport() {
    const projectId = this.getProjectId();
    const dashboardId = this.getDashboardId();

    if (!projectId || !dashboardId) {
      alert('Project ID or Dashboard ID not found');
      return;
    }

    this.isLoadingReports = true;
    this.cdr.markForCheck();

    dashGroupCollapsed('DashboardToolbar: loadLatestReport', { projectId, dashboardId });

    this.dashboardApiService.getLatestReport(projectId, dashboardId).subscribe({
      next: (report) => {
        dashLog('DashboardToolbar: latest report loaded', { id: (report as any)?.id, status: (report as any)?.status });
        this.isLoadingReports = false;
        this.cdr.markForCheck();
        
        if (!report) {
          alert('No reports found for this dashboard');
        } else {
          alert(`Latest report loaded successfully!\nReport ID: ${report.id}\nStatus: ${report.status}\nCreated: ${report.createdAt}`);
          this.reportChanged.emit(report as ReportDetail);
        }
        dashGroupEnd();
      },
      error: (error) => {
        dashError('DashboardToolbar: failed to load latest report', { message: (error as any)?.message, status: (error as any)?.status, response: safeStringify((error as any)?.error) });
        this.isLoadingReports = false;
        this.cdr.markForCheck();
        alert(`Error loading latest report: ${error.message || 'Unknown error'}`);
        dashGroupEnd();
      }
    });
  }
}

